import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Calendar, SlidersHorizontal } from 'lucide-react';
import { decisions, statutLabels, fileSuggestions } from '../data/mockData';
import './Recherche.css';

const juridictionsFilter = ['Toutes', 'TPI Abidjan', "Cour d'Appel Abidjan", 'TPI Bouaké', 'TPI Yamoussoukro'];
const periodeFilter = ['Toute période', 'Dernière semaine', 'Dernier mois', '3 derniers mois', '6 derniers mois', 'Année en cours'];

const Recherche: React.FC = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [selectedJuridiction, setSelectedJuridiction] = useState('Toutes');
  const [selectedPeriode, setSelectedPeriode] = useState('Toute période');
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = () => {
    if (query.trim()) {
      setHasSearched(true);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
    setHasSearched(true);
  };

  const results = hasSearched
    ? decisions.filter(d => {
        const matchSearch = query === '' ||
          d.numero.toLowerCase().includes(query.toLowerCase()) ||
          d.parties.toLowerCase().includes(query.toLowerCase()) ||
          d.nature.toLowerCase().includes(query.toLowerCase()) ||
          d.juridiction.toLowerCase().includes(query.toLowerCase());
        const matchJuridiction = selectedJuridiction === 'Toutes' || d.juridiction === selectedJuridiction;
        return matchSearch && matchJuridiction;
      })
    : [];

  return (
    <div className="recherche-page">
      {/* Search header */}
      <div className="recherche-header">
        <h2 className="recherche-title">Recherche dans la jurisprudence</h2>
        <div className="recherche-bar">
          <div className="recherche-input-wrapper">
            <Search size={18} className="recherche-input-icon" />
            <input
              type="text"
              placeholder="Rechercher par mots-clés, numéro de décision, parties..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <button className="btn-recherche" onClick={handleSearch}>
            Rechercher
          </button>
        </div>

        {/* Suggestions */}
        {!hasSearched && (
          <div className="recherche-suggestions">
            <span className="suggestions-label">Suggestions :</span>
            {fileSuggestions.map((s, i) => (
              <button key={i} className="suggestion-chip" onClick={() => handleSuggestionClick(s)}>
                {s}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="recherche-filters">
        <div className="filter-group">
          <select
            value={selectedJuridiction}
            onChange={(e) => setSelectedJuridiction(e.target.value)}
            className="recherche-select"
          >
            {juridictionsFilter.map(j => (
              <option key={j} value={j}>{j === 'Toutes' ? 'Toutes les juridictions' : j}</option>
            ))}
          </select>

          <div className="recherche-select-wrapper">
            <Calendar size={14} className="select-icon" />
            <select
              value={selectedPeriode}
              onChange={(e) => setSelectedPeriode(e.target.value)}
              className="recherche-select with-icon"
            >
              {periodeFilter.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>

          <button className="btn-filter-advanced">
            <SlidersHorizontal size={14} /> Filtres avancés
          </button>
        </div>
      </div>

      {/* Results */}
      {hasSearched && (
        <div className="recherche-results">
          <p className="recherche-results-count">
            {results.length} résultat{results.length > 1 ? 's' : ''} pour « {query} »
          </p>

          {results.length > 0 ? (
            <div className="recherche-results-list">
              {results.map(d => {
                const s = statutLabels[d.statut];
                return (
                  <div
                    key={d.id}
                    className="result-card"
                    onClick={() => navigate(`/espace-cndj/decisions/${d.id}`)}
                  >
                    <div className="result-card-header">
                      <span className="result-numero">{d.numero}</span>
                      <span className="status-badge" style={{ color: s.color, background: s.bg }}>
                        {s.label}
                      </span>
                    </div>
                    <div className="result-card-meta">
                      <span>{d.juridiction}</span>
                      <span className="meta-dot">·</span>
                      <span>{new Date(d.date).toLocaleDateString('fr-FR')}</span>
                      <span className="meta-dot">·</span>
                      <span>{d.nature}</span>
                    </div>
                    <p className="result-parties">{d.parties}</p>
                    <p className="result-resume">
                      Décision relative à un litige de {d.nature.toLowerCase()} impliquant les parties mentionnées.
                    </p>
                    <div className="result-tags">
                      <span className="result-tag">{d.nature.toLowerCase()}</span>
                      <span className="result-tag">{d.juridiction.split(' ').pop()?.toLowerCase()}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="recherche-empty">
              Aucun résultat trouvé pour votre recherche. Essayez d'élargir vos critères.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Recherche;
